import { useInjected } from '@bubblydoo/angular-react'
import { EdgeLabelRenderer, ReactFlowState, useStore, useStoreApi } from '@xyflow/react'
import { ReactElement } from 'react'

import { ResourceDocumentationToastMessages } from '@flowx/designer/shared/components/resource-documentation/resource-documentation.service'
import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'

import { EdgeDocumentationChanges, EdgeDocumentationPopover } from './EdgeDocumentationPopover'
import { EdgeDescriptionTarget, useEdgeDescription } from './useEdgeDescription'

type CanvasEdgeDocumentationProps = {
  /** The react-flow id of the edge, which can differ from the documented element id. */
  edgeId: string
  target: EdgeDescriptionTarget
  label: string
  /** Resolves to the id the owner lives at once the label is written, null when it was not. */
  saveLabel: (label: string) => Promise<string | null>
  /** The distance between the edge's midpoint and the popover, in canvas pixels. */
  gap: number
  labelX: number
  labelY: number
  readOnly: boolean
  onClose: () => void
}

const selectZoom = (state: ReactFlowState): number => state.transform[2]

// Mounted only while the edge is selected, so the zoom it reads does not re-render every edge.
export const CanvasEdgeDocumentation = ({
  edgeId,
  target,
  label,
  saveLabel,
  gap,
  labelX,
  labelY,
  readOnly,
  onClose,
}: CanvasEdgeDocumentationProps): ReactElement => {
  const toast = useInjected(ToastMessageService)
  const zoom = useStore(selectZoom)
  const flowStore = useStoreApi()
  const edge = useEdgeDescription(target, true, readOnly)

  const saveDescription = (description: string, ownerId?: string): void => {
    edge.save(description, ownerId).subscribe({
      error: (error) =>
        toast.showErrorMessage(
          error?.error?.title ?? ResourceDocumentationToastMessages.UPDATE_FAILURE
        ),
    })
  }

  // Either write may clone the owner, so the description waits for the label and goes to the id
  // the label was written to.
  const commit = ({ label: newLabel, description }: EdgeDocumentationChanges): void => {
    // Deleting the edge also unmounts the popover, which commits; the edge is gone by then.
    if (!flowStore.getState().edgeLookup.has(edgeId)) {
      return
    }

    if (newLabel === undefined) {
      if (description !== undefined) {
        saveDescription(description)
      }

      return
    }

    void saveLabel(newLabel).then((savedTo) => {
      if (description !== undefined) {
        saveDescription(description, savedTo ?? undefined)
      }
    })
  }

  return (
    <EdgeLabelRenderer>
      <div
        style={{
          position: 'absolute',
          transform: `translate(${labelX}px, ${labelY}px) scale(${1 / zoom}) translate(-50%, ${gap}px)`,
          transformOrigin: '0 0',
          pointerEvents: 'all',
          zIndex: 20,
        }}
      >
        <EdgeDocumentationPopover
          label={label}
          edge={edge}
          readOnly={readOnly}
          onCommit={commit}
          onClose={onClose}
        />
      </div>
    </EdgeLabelRenderer>
  )
}
